import {
    Button,
    ButtonColor,
    Container,
    ContainerSize,
    Gap,
    Heading,
    Stack,
    Text,
    Tooltip,
    TooltipPlacement,
} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

const TooltipPage = () => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Tooltip
            </Heading>

            <Text className="mb-4">
                Shows a short message when the user hovers or focuses the trigger. Pass <Text el="strong">text</Text> and wrap
                the trigger; press <Text el="strong">Escape</Text> to dismiss while open.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Usage
            </Heading>
            <Text className="mb-4">
                Hover or focus the trigger to show the message.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Tooltip text="Click to refresh">
                        <Button color={ButtonColor.Primary}>Hover me</Button>
                    </Tooltip>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {TooltipBasicCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Custom class
            </Heading>
            <Text className="mb-4">
                <Text el="strong">className</Text> applies to the tooltip root.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Tooltip text="Click to refresh" className="custom-tooltip">
                        <Button color={ButtonColor.Primary}>Hover me</Button>
                    </Tooltip>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {TooltipCustomClassCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Container
            </Heading>
            <Text className="mb-4">
                Default <Text el="strong">container=&quot;body&quot;</Text> portals the tooltip; use{" "}
                <Text el="strong">inline</Text> inside a <Text el="strong">position-relative</Text> parent when you need
                layout-anchored positioning.
            </Text>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Body (default)
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <Tooltip text="Rendered under document.body by default.">
                        <Button color={ButtonColor.Primary}>Default container</Button>
                    </Tooltip>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {TooltipPortalCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Inline
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <div className="position-relative">
                        <Tooltip container="inline" text="Anchored beside the trigger in the layout tree.">
                            <Button color={ButtonColor.Secondary}>Inline tooltip</Button>
                        </Tooltip>
                    </div>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {TooltipInlineCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Placement
            </Heading>
            <Text className="mb-4">
                Set <Text el="strong">TooltipPlacement</Text> on the component; examples use <Text el="strong">inline</Text>{" "}
                with a positioned parent.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <div className="position-relative">
                            <Tooltip container="inline" text="Tooltip on top." placement={TooltipPlacement.Top}>
                                <Button color={ButtonColor.Primary}>Tooltip on top</Button>
                            </Tooltip>
                        </div>
                        <div className="position-relative">
                            <Tooltip container="inline" text="Tooltip on end." placement={TooltipPlacement.End}>
                                <Button color={ButtonColor.Primary}>Tooltip on end</Button>
                            </Tooltip>
                        </div>
                        <div className="position-relative">
                            <Tooltip container="inline" text="Tooltip on bottom." placement={TooltipPlacement.Bottom}>
                                <Button color={ButtonColor.Primary}>Tooltip on bottom</Button>
                            </Tooltip>
                        </div>
                        <div className="position-relative">
                            <Tooltip container="inline" text="Tooltip on start." placement={TooltipPlacement.Start}>
                                <Button color={ButtonColor.Primary}>Tooltip on start</Button>
                            </Tooltip>
                        </div>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {TooltipPlacementCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                HTML content
            </Heading>
            <Text className="mb-4">
                Use <Text el="strong">isHtml</Text> only with trusted strings.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Tooltip text="<em>Tooltip</em> <u>with</u> <b>HTML</b>" isHtml={true}>
                        <Button color={ButtonColor.Primary}>Tooltip with HTML</Button>
                    </Tooltip>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {TooltipHTMLCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
};

export default TooltipPage;

const TooltipBasicCode = `<Tooltip text="Click to refresh">
    <Button color={ButtonColor.Primary}>Hover me</Button>
</Tooltip>`;

const TooltipCustomClassCode = `<Tooltip text="Click to refresh" className="custom-tooltip">
    <Button color={ButtonColor.Primary}>Hover me</Button>
</Tooltip>`;

const TooltipPortalCode = `<Tooltip text="Rendered under document.body by default.">
    <Button color={ButtonColor.Primary}>Default container</Button>
</Tooltip>`;

const TooltipInlineCode = `<div className="position-relative">
    <Tooltip container="inline" text="Anchored beside the trigger in the layout tree.">
        <Button color={ButtonColor.Secondary}>Inline tooltip</Button>
    </Tooltip>
</div>`;

const TooltipPlacementCode = `<Stack gap={Gap.Gap2}>
    <div className="position-relative">
        <Tooltip container="inline" text="Tooltip on top." placement={TooltipPlacement.Top}>
            <Button color={ButtonColor.Primary}>Tooltip on top</Button>
        </Tooltip>
    </div>
    <div className="position-relative">
        <Tooltip container="inline" text="Tooltip on end." placement={TooltipPlacement.End}>
            <Button color={ButtonColor.Primary}>Tooltip on end</Button>
        </Tooltip>
    </div>
    <div className="position-relative">
        <Tooltip container="inline" text="Tooltip on bottom." placement={TooltipPlacement.Bottom}>
            <Button color={ButtonColor.Primary}>Tooltip on bottom</Button>
        </Tooltip>
    </div>
    <div className="position-relative">
        <Tooltip container="inline" text="Tooltip on start." placement={TooltipPlacement.Start}>
            <Button color={ButtonColor.Primary}>Tooltip on start</Button>
        </Tooltip>
    </div>
</Stack>`;

const TooltipHTMLCode = `<Tooltip text="<em>Tooltip</em> <u>with</u> <b>HTML</b>" isHtml={true}>
    <Button color={ButtonColor.Primary}>Tooltip with HTML</Button>
</Tooltip>`;
